import { useMemo } from 'react';

import type { DomainQueryOptions } from '@/domain/shared/types';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { useCompareController_getLocationCompare } from '@/services/hooks';
import type { LocationCompareResponseDto } from '@/services/types';

import { mapLocationCompareResponseToDomain } from './mappers';
import type {
    LocationCompareData,
    LocationCompareParams,
    UseGetLocationCompareResult,
} from './types';

const EMPTY_RESPONSE: LocationCompareResponseDto = {
    locationId: 0,
    locationName: '',
    cages: [],
    metrics: {},
};

const EMPTY_DATA: LocationCompareData = {
    locationId: 0,
    locationName: '',
    cages: [],
    metrics: {},
};

/**
 * Domain hook to fetch compare data for a location.
 *
 * The response is grouped by module (biomass, lice, wounds, maturation, behavior, environment).
 * Licensed groups that the location is not licensed for are omitted by the backend.
 * Environment is not licence-gated and is omitted from the payload when no cage
 * has data (or when a given param has no values). The compare page still lists
 * all environmental params as disabled options in that case — do not derive the
 * dropdown solely from `data.metrics` keys.
 *
 * With `fish-group-tracking` on, the request asks for fish groups: a cage holding an
 * active group is compared on that group's data, and says which group it is. The flag is read
 * here, as `useGetClientOverview` does, so no caller has to pass it.
 */
export const useGetLocationCompare = (
    clientId: number,
    locationId: number,
    params?: LocationCompareParams,
    options?: DomainQueryOptions<LocationCompareResponseDto>
): UseGetLocationCompareResult => {
    const { enabled: fishGroupsEnabled } = usePostHogFeatureFlag('fish-group-tracking');

    const query = useCompareController_getLocationCompare(
        clientId,
        locationId,
        // Sent only when true, so the flag-off request keeps the query key it always had.
        fishGroupsEnabled ? { ...params, includeFishGroups: true } : params,
        options
    );

    const data = useMemo(
        () => mapLocationCompareResponseToDomain(query.data?.data ?? EMPTY_RESPONSE),
        [query.data?.data]
    );

    return {
        ...query,
        isLoading: query.isPending,
        data: query.data?.data ? data : EMPTY_DATA,
    };
};
